import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, ArrowUpRightIcon, CheckIcon, InfoIcon, MessageCircleIcon } from 'lucide-react';
import { DosingInteractive } from './DosingInteractive';
import { EvidenceInteractive } from './EvidenceInteractive';
import { MoleculeInteractive } from './MoleculeInteractive';
import { PenInteractive } from './PenInteractive';
import { boothChapters, boothSteps, selfHints } from '../../data/booth';
import type { BoothChapter, BoothMode } from '../../types/booth';
interface BoothChapterViewProps {
  chapter: BoothChapter;
  mode: BoothMode;
  visited: string[];
  onOpen: (chapterId: string) => void;
  onHome: () => void;
}
const ease = [0.22, 1, 0.36, 1] as const;
export function BoothChapterView({
  chapter,
  mode,
  visited,
  onOpen,
  onHome
}: BoothChapterViewProps) {
  const [step, setStep] = useState(0);
  const [poll, setPoll] = useState<number | null>(null);
  const stepId = boothSteps[step].id;
  const {
    accent
  } = chapter;
  const others = boothChapters.filter(c => c.id !== chapter.id).sort((a, b) => Number(visited.includes(a.id)) - Number(visited.includes(b.id)));
  const Interactive = {
    dosing: DosingInteractive,
    molecule: MoleculeInteractive,
    evidence: EvidenceInteractive,
    pen: PenInteractive
  }[chapter.interactive];
  return <div className="flex h-full" data-id="element-2" data-mp="1orarwjirh2ox">
      <aside className="relative w-[32%] shrink-0 overflow-hidden bg-mist-100" data-id="element-3" data-mp="1oscbjcduexi9">
        <motion.img src={chapter.image} alt="" initial={{
        scale: 1.15
      }} animate={{
        scale: 1.02
      }} transition={{
        duration: 9,
        ease: 'easeOut'
      }} className="absolute inset-0 h-full w-full object-cover" data-id="element-4" data-mp="1k02e7baeev5y" />
        <div className="absolute inset-x-5 bottom-5 rounded-2xl bg-white/90 p-4 backdrop-blur-md" data-id="element-5" data-mp="1ay2zwqqrq56p">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{
          color: accent
        }} data-id="element-6" data-mp="162nos6k5andz">
            Chapter {chapter.number} · {chapter.kicker}
          </p>
          <p className="mt-1 font-serif text-[22px] leading-tight text-navy-900" data-id="element-7" data-mp="rcsz0m5j32aa">{chapter.title}</p>
        </div>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col" data-id="element-8" data-mp="hfy1o6kzr7xl">
        <nav aria-label="Chapter steps" className="flex items-center gap-2 px-9 pt-6" data-id="element-9" data-mp="hhhgrtuwvcxs">
          {boothSteps.map((s, i) => {
          const reachable = i <= step || i === 1 && poll !== null;
          return <button key={s.id} type="button" disabled={!reachable} onClick={() => setStep(i)} aria-current={i === step ? 'step' : undefined} className="flex items-center gap-2 rounded-full px-3 py-1.5 text-[12px] font-medium transition disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" style={{
            backgroundColor: i === step ? `${accent}14` : 'transparent',
            color: i === step ? accent : i < step ? '#0F2442' : '#94A3B8'
          }} data-id="element-10" data-mp="1a6oq7nvwlmxt" data-mpi={i}>
                <span className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold" style={{
              backgroundColor: i <= step ? accent : '#E1ECEC',
              color: i <= step ? '#FFFFFF' : '#64748B'
            }} data-id="element-11" data-mp="3dkgznf4y3a3s">
                  {i < step ? <CheckIcon className="h-3 w-3" data-id="element-12" data-mp="2zntqmdzpwthi" /> : i + 1}
                </span>
                {s.label}
              </button>;
        })}
        </nav>

        <div className="min-h-0 flex-1 overflow-y-auto px-9 pb-6 pt-6 no-scrollbar" data-id="element-13" data-mp="38n72tef87w5x">
          <AnimatePresence mode="wait" data-id="element-14" data-mp="34jr6ykazdzua">
            <motion.div key={stepId} initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.4,
            ease
          }} data-id="element-15" data-mp="34mww1zjm11q0">
              {stepId === 'question' && <div data-id="element-16" data-mp="1f6kwh1osf6mv">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{
                color: accent
              }} data-id="element-17" data-mp="1j6d67hpx3itt">
                    A clinical question
                  </p>
                  <h2 className="mt-3 max-w-[560px] font-serif text-[32px] leading-[1.1] text-navy-900" data-id="element-18" data-mp="1o44zaf2atazl">{chapter.question}</h2>
                  <ul className="mt-7 grid grid-cols-2 gap-3" data-id="element-19" data-mp="1y3lzigys6wo6">
                    {chapter.pollOptions.map((o, i) => {
                  const on = poll === i;
                  return <li key={o} data-id="element-20" data-mp="26vxsoala4y7u" data-mpi={i}>
                          <button type="button" onClick={() => setPoll(i)} aria-pressed={on} className="flex min-h-[64px] w-full items-center gap-3 rounded-2xl border bg-white px-4 py-3 text-left transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" style={{
                      borderColor: on ? accent : 'rgba(15,36,66,0.1)',
                      backgroundColor: on ? `${accent}12` : '#FFFFFF'
                    }} data-id="element-21" data-mp="26wz2kbqk7enz">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold" style={{
                        borderColor: on ? accent : 'rgba(15,36,66,0.15)',
                        backgroundColor: on ? accent : 'transparent',
                        color: on ? '#FFFFFF' : '#4A6489'
                      }} data-id="element-22" data-mp="75gr0ntfxu1">
                              {on ? <CheckIcon className="h-3.5 w-3.5" data-id="element-23" data-mp="e7zp5jav5ysp" /> : String.fromCharCode(65 + i)}
                            </span>
                            <span className="text-[15px] font-medium text-navy-900" data-id="element-24" data-mp="1boeztm318nut">{o}</span>
                          </button>
                        </li>;
                })}
                  </ul>
                  {poll !== null && <motion.p initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} className="mt-6 max-w-[560px] text-[16px] leading-relaxed text-slate-600" data-id="element-25" data-mp="363kdznlldw61">
                      {chapter.pollBridge}
                    </motion.p>}
                </div>}

              {stepId === 'science' && <div data-id="element-26" data-mp="2mfustz85uu5s">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{
                color: accent
              }} data-id="element-27" data-mp="2mgeadetwfgja">
                    The science
                  </p>
                  <h2 className="mt-3 font-serif text-[30px] leading-[1.1] text-navy-900" data-id="element-28" data-mp="2dfg6cwjgn90p">{chapter.scienceTitle}</h2>
                  <ul className="mt-7 grid grid-cols-3 gap-4" data-id="element-29" data-mp="6ogp009hnoww">
                    {chapter.points.map((p, i) => <motion.li key={p.title} initial={{
                  opacity: 0,
                  y: 14
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.5,
                  delay: 0.15 + i * 0.12,
                  ease
                }} className={`rounded-3xl p-5 ${p.placeholder ? 'border border-dashed border-navy-900/20 bg-mist-50' : 'border border-navy-900/[0.08] bg-white'}`} data-id="element-30" data-mp="bkxp7styptqc" data-mpi={i}>
                        <span className="font-serif text-[28px] leading-none" style={{
                    color: accent
                  }} data-id="element-31" data-mp="2euiy3z9g5qic">
                          0{i + 1}
                        </span>
                        <p className="mt-3 text-[15px] font-semibold text-navy-900" data-id="element-32" data-mp="2otghkns0mtuh">{p.title}</p>
                        <p className={`mt-2 text-[13px] leading-relaxed ${p.placeholder ? 'italic text-slate-400' : 'text-slate-600'}`} data-id="element-33" data-mp="2juwvmndf9i24">
                          {p.body}
                        </p>
                      </motion.li>)}
                  </ul>
                </div>}

              {stepId === 'explore' && <div data-id="element-34" data-mp="3n6qk06jxuja">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{
                color: accent
              }} data-id="element-35" data-mp="3lm4z027tlxk">
                    Explore
                  </p>
                  <h2 className="mt-3 font-serif text-[28px] leading-[1.1] text-navy-900" data-id="element-36" data-mp="ddryie848bev">{chapter.exploreTitle}</h2>
                  <p className="mt-2 text-[14px] text-slate-500" data-id="element-37" data-mp="ibjsddsbm7lp">{chapter.exploreIntro}</p>
                  <div className="mt-6" data-id="element-38" data-mp="3gvtbf1sfwcbw">
                    <Interactive accent={accent} data-id="element-39" data-mp="3lstgpahgtb1y" />
                  </div>
                </div>}

              {stepId === 'takeaway' && <div data-id="element-40" data-mp="3qqv0jxmpteeh">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{
                color: accent
              }} data-id="element-41" data-mp="3quiu2ws5u0t7">
                    Key takeaway
                  </p>
                  <h2 className="mt-3 max-w-[600px] font-serif text-[38px] leading-[1.06] text-navy-900" data-id="element-42" data-mp="161e05gxs95z3">{chapter.takeaway}</h2>
                  <p className="mt-8 text-[12px] font-semibold uppercase tracking-[0.16em] text-slate-400" data-id="element-43" data-mp="1a5ddjqnrnoo8">Explore next</p>
                  <ul className="mt-3 grid grid-cols-3 gap-3" data-id="element-44" data-mp="2qk84cz4860j1">
                    {others.map(c => <li key={c.id} data-id="element-45" data-mp="2lspz3yv65ds9">
                        <button type="button" onClick={() => onOpen(c.id)} className="group flex w-full items-center gap-3 rounded-2xl border border-navy-900/[0.08] bg-white p-3 text-left transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" data-id="element-46" data-mp="2lroqe11ml3dp">
                          <img src={c.image} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" data-id="element-47" data-mp="22v6pat0vzr4s" />
                          <span className="min-w-0 flex-1" data-id="element-48" data-mp="1xwvfgialdgmk">
                            <span className="block text-[10px] font-semibold uppercase tracking-[0.14em]" style={{
                        color: c.accent
                      }} data-id="element-49" data-mp="13k2z4rxwken">
                              {c.number}
                              {visited.includes(c.id) ? ' · Explored' : ''}
                            </span>
                            <span className="block truncate text-[14px] font-medium text-navy-900" data-id="element-50" data-mp="29yn7txev3ap7">{c.title}</span>
                          </span>
                          <ArrowUpRightIcon className="h-4 w-4 shrink-0 text-navy-700" data-id="element-51" data-mp="2nxmnwgob6h9r" />
                        </button>
                      </li>)}
                  </ul>
                  <p className="mt-6 text-[11px] text-slate-400" data-id="element-52" data-mp="1vaac91jortv8">References: [1] [2] [3] — to be completed. Prescribing information available at the booth.</p>
                </div>}
            </motion.div>
          </AnimatePresence>
        </div>

        <footer className="flex items-center gap-4 border-t border-navy-900/[0.06] bg-white/80 px-9 py-4 backdrop-blur-md" data-id="element-53" data-mp="208txt0udb3n2">
          <AnimatePresence mode="wait" data-id="element-54" data-mp="1chxzdkvepa6u">
            <motion.div key={`${mode}-${stepId}`} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} className="min-w-0 flex-1" data-id="element-55" data-mp="1cgwl77j3g7kw">
              {mode === 'rep' ? <p className="flex items-start gap-2.5 rounded-2xl bg-navy-900 px-4 py-2.5 text-[12px] leading-relaxed text-white" data-id="element-56" data-mp="3nzjfnhkffgw7">
                  <MessageCircleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-300" data-id="element-57" data-mp="3f5o6zzk3j8bt" />
                  <span data-id="element-58" data-mp="3f3aarndojq2o">
                    <span className="font-semibold" data-id="element-59" data-mp="6pbjv05keuw1">Rep guide · </span>
                    {chapter.repPrompts[stepId]}
                  </span>
                </p> : <p className="flex items-center gap-2 text-[12px] text-slate-500" data-id="element-60" data-mp="3sx1nt5qmds35">
                  <InfoIcon className="h-3.5 w-3.5 shrink-0" data-id="element-61" data-mp="3szfgj7xtus7j" />
                  {selfHints[stepId]}
                </p>}
            </motion.div>
          </AnimatePresence>
          <div className="flex shrink-0 items-center gap-2" data-id="element-62" data-mp="2hgl6t7zf33e6">
            <button type="button" onClick={() => step === 0 ? onHome() : setStep(step - 1)} className="flex h-11 items-center gap-1.5 rounded-full border border-navy-900/10 px-4 text-[13px] font-medium text-navy-800 transition hover:bg-mist-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" data-id="element-63" data-mp="2mi0p4sa7mvsv">
              <ArrowLeftIcon className="h-4 w-4" data-id="element-64" data-mp="1onq2nz308md9" />
              {step === 0 ? 'All chapters' : 'Back'}
            </button>
            <button type="button" onClick={() => step === boothSteps.length - 1 ? onHome() : setStep(step + 1)} disabled={stepId === 'question' && poll === null} className="flex h-11 items-center gap-1.5 rounded-full bg-navy-900 px-5 text-[13px] font-semibold text-white transition hover:bg-navy-800 disabled:cursor-not-allowed disabled:bg-navy-900/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris focus-visible:ring-offset-2" data-id="element-65" data-mp="1jpg2fa2favks">
              {step === boothSteps.length - 1 ? 'All chapters' : `Next: ${boothSteps[step + 1].label}`}
              <ArrowRightIcon className="h-4 w-4" style={{
              color: accent
            }} data-id="element-66" data-mp="37lw0p13s9895" />
            </button>
          </div>
        </footer>
      </section>
    </div>;
}